import { useCallback, useEffect, useRef, useState } from 'react';
import {
  addDoc,
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import { bounceFirestoreNetwork, db } from '../firebase';
import * as api from './api';
import { detectCountry } from './locale';
import { savedNameColor } from './cosmetics';
import { savedFrame } from './shop';
import { track } from '../analytics';

// Inferred once from the browser locale; sent with create/join for the flag.
const COUNTRY = detectCountry();
import {
  needsTarget,
  parseLogEvent,
  type Card,
  type GameState,
  type PlayerAction,
  type PrivateState,
  type PublicPlayer,
  type Role,
  type Room,
  type RoomStatus,
} from './types';

export type Screen = 'home' | 'lobby' | 'roleReveal' | 'table' | 'gameOver';

export interface Emote {
  id: string;
  uid: string;
  /** Either a single emoji glyph or a short text message (mutually exclusive). */
  emoji?: string;
  text?: string;
  /** i18n key for a bot line — resolved to localized copy at render time. */
  lineKey?: string;
}

/** One entry in the table feed: either a system log line or a chat message. */
export interface FeedItem {
  id: string;
  /** System event: a raw turnLog line (JSON LogEvent) — render via renderLogLine. */
  line?: string;
  /** Chat message: the sender plus one of text/emoji/lineKey. */
  uid?: string;
  text?: string;
  emoji?: string;
  lineKey?: string;
}

export interface TableFX {
  id: number;
  kind:
    | { type: 'spike'; fromUid: string; toUid: string }
    | { type: 'blocked'; uid: string }
    | { type: 'damage'; uid: string; amount: number }
    | { type: 'heal'; uid: string }
    /** A runner flatlined — role now public. Drives the reveal ceremony. */
    | { type: 'death'; uid: string; role: Role }
    /** Trace exposed a living runner's role. */
    | { type: 'reveal'; uid: string; role: Role }
    /** EMP detonated by fromUid — everyone else is fried. */
    | { type: 'emp'; fromUid: string }
    /** A planted Virus ticked on its carrier. */
    | { type: 'plantTick'; uid: string }
    /** The turn passed from one living seat to another. */
    | { type: 'handoff'; fromUid: string; toUid: string };
}

/** Dramatic full-screen beats that happen TO the local player. */
export type ScreenEventKind =
  | 'shield'
  | 'firewall'
  | 'free'
  | 'reboot'
  | 'locked'
  | 'bounty'
  | 'penalty';
export interface ScreenEvent {
  id: number;
  kind: ScreenEventKind;
}

const SAVED_CODE_KEY = 'neonfall.activeRoomCode';

/**
 * Live session state, mirroring the Firestore documents. Ports the iOS
 * AppModel: Cloud Function callables mutate; Firestore snapshot listeners
 * render. There is zero game logic here — the server reducer owns all of it.
 */
export function useGameSession(
  uid: string | null,
  displayName: string,
  photoURL: string | null,
) {
  const [screen, setScreen] = useState<Screen>('home');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [room, setRoom] = useState<Room | null>(null);
  const [roomLoaded, setRoomLoaded] = useState(false);
  const [players, setPlayers] = useState<PublicPlayer[]>([]);
  const [game, setGame] = useState<GameState | null>(null);
  const [priv, setPriv] = useState<PrivateState | null>(null);
  const [fx, setFx] = useState<TableFX[]>([]);
  const [screenFx, setScreenFx] = useState<ScreenEvent[]>([]);
  const [emotes, setEmotes] = useState<Emote[]>([]);
  const [feed, setFeed] = useState<FeedItem[]>([]);
  // True while watching a room we never joined (read-only, no seat).
  const [spectating, setSpectating] = useState(false);

  const roomIdRef = useRef<string | null>(null);
  const photoURLRef = useRef(photoURL);
  photoURLRef.current = photoURL;
  const unsubsRef = useRef<Array<() => void>>([]);
  const resumeToTableRef = useRef(false);
  const lastLogRef = useRef<string | null>(null);
  const prevPlayersRef = useRef<PublicPlayer[]>([]);
  const prevActiveSeatRef = useRef<number | null>(null);
  const fxIdRef = useRef(0);
  const screenFxIdRef = useRef(0);
  const gameOverTrackedRef = useRef(false);
  const chatReadyRef = useRef(false);
  const feedIdRef = useRef(0);

  const myUid = uid ?? '';

  const emitFx = useCallback((kind: TableFX['kind']) => {
    fxIdRef.current += 1;
    const entry = { id: fxIdRef.current, kind };
    setFx((q) => [...q.slice(-11), entry]);
  }, []);

  const emitScreen = useCallback((kind: ScreenEventKind) => {
    screenFxIdRef.current += 1;
    const entry = { id: screenFxIdRef.current, kind };
    setScreenFx((q) => [...q.slice(-3), entry]);
  }, []);

  const pushFeed = useCallback((items: FeedItem[]) => {
    setFeed((f) => [...f, ...items].slice(-160));
  }, []);

  const detach = useCallback(() => {
    for (const u of unsubsRef.current) u();
    unsubsRef.current = [];
    roomIdRef.current = null;
  }, []);

  // --- Firestore listeners --------------------------------------------------

  const attach = useCallback(
    (roomId: string) => {
      detach();
      roomIdRef.current = roomId;
      lastLogRef.current = null;
      prevPlayersRef.current = [];
      prevActiveSeatRef.current = null;
      chatReadyRef.current = false;
      const roomDoc = doc(db, 'rooms', roomId);

      unsubsRef.current.push(
        onSnapshot(roomDoc, (snap) => {
          if (!snap.exists()) return;
          const d = snap.data();
          setRoom({
            id: snap.id,
            code: (d.code as string) ?? '------',
            hostUid: (d.hostUid as string) ?? '',
            status: (d.status as RoomStatus) ?? 'lobby',
            turnLimit: (d.turnLimit as number) ?? 60,
            botDifficulty: (d.botDifficulty as string) ?? 'normal',
            isPublic: d.isPublic === true,
            seed: typeof d.seed === 'string' ? d.seed : undefined,
          });
          setRoomLoaded(true); // real room data has arrived (vs the optimistic set)
        }),
      );

      unsubsRef.current.push(
        onSnapshot(
          query(collection(roomDoc, 'players'), orderBy('seatIndex')),
          (snap) => {
            const next = snap.docs.map((d) => d.data() as PublicPlayer);
            // Always show my own photo from the signed-in account, even in games
            // whose player doc predates photoURL support.
            const mine = photoURLRef.current;
            if (mine) {
              for (const p of next) if (p.uid === myUid && !p.photoURL) p.photoURL = mine;
            }
            // Derive damage/heal FX from integrity diffs (only once a game
            // has dealt integrity, matching the iOS guard).
            for (const p of next) {
              const prev = prevPlayersRef.current.find((x) => x.uid === p.uid);
              if (!prev || prev.maxIntegrity === 0 || prev.maxIntegrity !== p.maxIntegrity) continue;
              if (p.integrity < prev.integrity) {
                emitFx({ type: 'damage', uid: p.uid, amount: prev.integrity - p.integrity });
              } else if (p.integrity > prev.integrity) {
                emitFx({ type: 'heal', uid: p.uid });
              }
            }
            prevPlayersRef.current = next;
            setPlayers(next);
          },
        ),
      );

      unsubsRef.current.push(
        onSnapshot(doc(collection(roomDoc, 'state'), 'current'), (snap) => {
          if (!snap.exists()) return;
          const g = snap.data() as GameState;
          // Derive spike/block FX from new log lines (sliding 40-line tail).
          // The first snapshot seeds the feed with the visible history but
          // fires no FX, so joining/reloading doesn't replay old effects.
          const log = g.turnLog ?? [];
          if (log.length) {
            const anchor = lastLogRef.current;
            const fresh = anchor ? log.slice(log.lastIndexOf(anchor) + 1) : log;
            if (anchor) {
              for (const raw of fresh) {
                const ev = parseLogEvent(raw);
                if (!ev) continue;
                if (ev.t === 'spike') emitFx({ type: 'spike', fromUid: ev.a, toUid: ev.b });
                else if (ev.t === 'fw' || ev.t === 'shield') emitFx({ type: 'blocked', uid: ev.p });
                else if (ev.t === 'flat') emitFx({ type: 'death', uid: ev.p, role: ev.r });
                else if (ev.t === 'trace') emitFx({ type: 'reveal', uid: ev.b, role: ev.r });
                else if (ev.t === 'emp') emitFx({ type: 'emp', fromUid: ev.p });
                else if (ev.t === 'virus') emitFx({ type: 'plantTick', uid: ev.p });
                // Full-screen beats for events that happen to ME.
                if ('p' in ev && ev.p === myUid) {
                  if (ev.t === 'shield') emitScreen('shield');
                  else if (ev.t === 'fw') emitScreen('firewall');
                  else if (ev.t === 'dtFree') emitScreen('free');
                  else if (ev.t === 'reboot') emitScreen('reboot');
                  else if (ev.t === 'dtSkip') emitScreen('locked');
                  else if (ev.t === 'bounty') emitScreen('bounty');
                  else if (ev.t === 'penalty') emitScreen('penalty');
                }
              }
            }
            if (fresh.length) {
              pushFeed(fresh.map((line) => ({ id: 'log' + ++feedIdRef.current, line })));
            }
            lastLogRef.current = log[log.length - 1] ?? null;
          }
          // Turn handoff: a signal pulse travels from the seat that just ended
          // to the one that begins. Skip the first snapshot and finished games.
          const prevSeat = prevActiveSeatRef.current;
          if (
            prevSeat !== null &&
            prevSeat !== g.activeSeat &&
            !g.winner &&
            g.phase !== 'finished'
          ) {
            const roster = prevPlayersRef.current;
            const from = roster.find((p) => p.seatIndex === prevSeat);
            const to = roster.find((p) => p.seatIndex === g.activeSeat);
            if (from && to) emitFx({ type: 'handoff', fromUid: from.uid, toUid: to.uid });
          }
          prevActiveSeatRef.current = g.activeSeat;
          setGame(g);
        }),
      );

      if (myUid) {
        unsubsRef.current.push(
          onSnapshot(doc(collection(roomDoc, 'private'), myUid), (snap) => {
            if (!snap.exists()) return;
            setPriv(snap.data() as PrivateState);
          }),
        );
      }

      // Ephemeral chat: only NEW emotes (after we subscribe) float up; existing
      // history on first snapshot is ignored so we don't replay old trash-talk.
      unsubsRef.current.push(
        onSnapshot(
          query(collection(roomDoc, 'chat'), orderBy('at', 'desc'), limit(20)),
          (snap) => {
            if (!chatReadyRef.current) {
              chatReadyRef.current = true;
              return;
            }
            for (const change of snap.docChanges()) {
              if (change.type !== 'added') continue;
              const d = change.doc.data();
              const emote: Emote = {
                id: change.doc.id,
                uid: d.uid,
                emoji: d.emoji,
                text: d.text,
                lineKey: d.botLine,
              };
              if (!emote.emoji && !emote.text && !emote.lineKey) continue;
              // Every message lands in the persistent feed AND floats at the
              // sender's seat for a moment.
              pushFeed([{ ...emote }]);
              setEmotes((list) => [...list.slice(-11), emote]);
              const id = emote.id;
              // Text/bot lines linger a little longer than an emoji so they can be read.
              const isText = emote.text || emote.lineKey;
              setTimeout(() => setEmotes((list) => list.filter((e) => e.id !== id)), isText ? 5000 : 3500);
            }
          },
        ),
      );
    },
    [detach, emitFx, emitScreen, pushFeed, myUid],
  );

  useEffect(() => () => detach(), [detach]);

  // --- Navigation: react to whatever the listeners deliver ------------------

  useEffect(() => {
    if (!room) return;
    // Spectators never join a seat, so the lobby/role-reveal machinery doesn't
    // apply: they sit on the table and only advance to the result when the game
    // ends.
    if (spectating) {
      if (game?.winner && screen === 'table') setScreen('gameOver');
      return;
    }
    // Rematch rewinds a finished run back to the lobby.
    if (screen === 'gameOver' && !game?.winner && room.status === 'playing') {
      setScreen('lobby');
      return;
    }
    // Resume after a refresh: only decide once the REAL room snapshot has loaded
    // (not the optimistic lobby placeholder), so we don't flash the role reveal.
    if (resumeToTableRef.current && roomLoaded) {
      if (room.status === 'finished') {
        resumeToTableRef.current = false;
        void leaveToHome();
        return;
      }
      if (room.status === 'playing') {
        if (priv) {
          resumeToTableRef.current = false;
          setScreen('table'); // already saw the role — jump straight back in
        }
        return; // playing but role not loaded yet: wait, don't show roleReveal
      }
      resumeToTableRef.current = false; // genuinely still in the lobby
    }
    if (room.status === 'playing' && priv && screen === 'lobby') {
      setScreen('roleReveal');
    }
    if (game?.winner && (screen === 'table' || screen === 'roleReveal')) {
      setScreen('gameOver');
    }
    // `screen` is a dependency so chained transitions (e.g. rematch:
    // gameOver → lobby → roleReveal) complete without waiting on another
    // Firestore snapshot to re-fire the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room?.status, priv, game?.winner, roomLoaded, screen, spectating]);

  // --- Room lifecycle -------------------------------------------------------

  const openRoom = useCallback(
    (handle: api.RoomHandle) => {
      setSpectating(false);
      setRoom({
        id: handle.roomId,
        code: handle.code,
        hostUid: '',
        status: 'lobby',
        turnLimit: 60,
        botDifficulty: 'normal',
      });
      setRoomLoaded(false);
      setPlayers([]);
      setGame(null);
      setPriv(null);
      setFx([]);
      setFeed([]);
      attach(handle.roomId);
      localStorage.setItem(SAVED_CODE_KEY, handle.code);
      setScreen('lobby');
    },
    [attach],
  );

  /** Watch a public game in progress without taking a seat — read-only. No
   *  join callable, no saved-code resume; just attach the listeners the rules
   *  now allow for public rooms and drop onto the table. */
  const spectate = useCallback(
    (roomId: string) => {
      setSpectating(true);
      setRoom({ id: roomId, code: '', hostUid: '', status: 'playing', turnLimit: 60, botDifficulty: 'normal' });
      setRoomLoaded(false);
      setPlayers([]);
      setGame(null);
      setPriv(null);
      setFx([]);
      setFeed([]);
      attach(roomId);
      setScreen('table');
      track('spectate');
    },
    [attach],
  );

  const run = useCallback(async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, []);

  const createRoom = useCallback(
    (turnLimit = 60, botDifficulty = 'normal') => run(async () => {
      openRoom(
        await api.createRoom(
          displayName, photoURL, turnLimit, botDifficulty, COUNTRY, savedNameColor(), savedFrame(),
        ),
      );
      track('create_room', { turnLimit, botDifficulty });
    }),
    [run, openRoom, displayName, photoURL],
  );

  const joinRoom = useCallback(
    (code: string) => run(async () => {
      openRoom(
        await api.joinRoom(code, displayName, photoURL, COUNTRY, savedNameColor(), savedFrame()),
      );
      track('join_room');
    }),
    [run, openRoom, displayName, photoURL],
  );

  // One tap from Home into a live match: room + 4 bots + immediate start.
  // Bots are added sequentially — each addBot transaction reads the seat count.
  const quickPlay = useCallback(
    () => run(async () => {
      const handle = await api.createRoom(
        displayName, photoURL, 60, 'normal', COUNTRY, savedNameColor(), savedFrame(),
      );
      openRoom(handle);
      for (let i = 0; i < 4; i++) await api.addBot(handle.roomId);
      await api.startGame(handle.roomId);
      track('quick_play');
    }),
    [run, openRoom, displayName, photoURL],
  );

  const leaveToHome = useCallback(async () => {
    localStorage.removeItem(SAVED_CODE_KEY);
    const roomId = roomIdRef.current;
    const wasSpectating = spectating;
    detach();
    setSpectating(false);
    setRoom(null);
    setRoomLoaded(false);
    setPlayers([]);
    setGame(null);
    setPriv(null);
    setFx([]);
    setFeed([]);
    setScreen('home');
    // Spectators never held a seat, so there's nothing to leave server-side.
    if (roomId && !wasSpectating) {
      try {
        await api.leaveRoom(roomId);
      } catch {
        // best-effort — we're leaving regardless
      }
    }
  }, [detach, spectating]);

  const startGame = useCallback(
    () => run(async () => {
      if (roomIdRef.current) await api.startGame(roomIdRef.current);
      track('game_started', { players: prevPlayersRef.current.length || undefined });
    }),
    [run],
  );

  const rematch = useCallback(
    () => run(async () => {
      if (roomIdRef.current) await api.rematch(roomIdRef.current);
      track('game_started', { rematch: true });
    }),
    [run],
  );

  const submit = useCallback(
    (action: PlayerAction) =>
      run(async () => {
        if (roomIdRef.current) await api.submitAction(roomIdRef.current, action);
      }),
    [run],
  );

  const addBot = useCallback(
    () => run(async () => {
      if (roomIdRef.current) await api.addBot(roomIdRef.current);
    }),
    [run],
  );

  const removeBot = useCallback(
    (botUid: string) => run(async () => {
      if (roomIdRef.current) await api.removeBot(roomIdRef.current, botUid);
    }),
    [run],
  );

  // Host adjusts match settings in the lobby. Patch locally for a snappy toggle;
  // the room snapshot will confirm (or correct) it moments later.
  const updateRoomSettings = useCallback(
    (settings: { turnLimit?: number; botDifficulty?: string; isPublic?: boolean }) => {
      const roomId = roomIdRef.current;
      if (!roomId) return;
      setRoom((r) => (r ? { ...r, ...settings } : r));
      api.updateRoomSettings(roomId, settings).catch((err) => {
        setError((err as Error).message);
      });
    },
    [],
  );

  const lastEmoteRef = useRef(0);
  const post = useCallback((payload: { emoji: string } | { text: string }) => {
    const roomId = roomIdRef.current;
    if (!roomId || !myUid) return;
    const now = performance.now();
    if (now - lastEmoteRef.current < 400) return; // light anti-spam
    lastEmoteRef.current = now;
    addDoc(collection(doc(db, 'rooms', roomId), 'chat'), {
      uid: myUid,
      ...payload,
      at: serverTimestamp(),
    }).catch(() => {
      /* best-effort */
    });
  }, [myUid]);
  const sendEmote = useCallback((emoji: string) => post({ emoji }), [post]);
  const sendMessage = useCallback((text: string) => {
    const trimmed = text.trim().slice(0, 120);
    if (trimmed) post({ text: trimmed });
  }, [post]);

  // Reconnect to a room from a previous visit, if any. This is a SILENT resume:
  // it must never flip the busy flag or raise an error toast, so a stale or
  // cleaned-up room can't wedge the app on reload — on failure we just stay home.
  const resumeIfPossible = useCallback(() => {
    if (screen !== 'home' || !myUid) return;
    const code = localStorage.getItem(SAVED_CODE_KEY);
    if (!code) return;
    resumeToTableRef.current = true;
    api.joinRoom(code, displayName, photoURL, COUNTRY)
      .then(openRoom)
      .catch(() => {
        resumeToTableRef.current = false;
      });
  }, [screen, myUid, displayName, photoURL, openRoom]);

  // --- Derived --------------------------------------------------------------

  const me = players.find((p) => p.uid === myUid) ?? null;
  const isHost = !!room && room.hostUid === myUid;

  // Analytics: screen funnel + one game_over per finished game (host reports it
  // so a game is counted once, not once per player).
  useEffect(() => {
    track('screen_view', { firebase_screen: screen });
  }, [screen]);
  useEffect(() => {
    if (game?.winner && isHost && !gameOverTrackedRef.current) {
      gameOverTrackedRef.current = true;
      track('game_over', { winner: game.winner });
    }
    if (!game?.winner) gameOverTrackedRef.current = false;
  }, [game?.winner, isHost]);

  // Turn timer: when the visible deadline passes, ask the server to auto-pass
  // the stalled turn. Every client schedules this (server is idempotent); a
  // small random jitter avoids a thundering herd.
  useEffect(() => {
    const deadline = game?.turnDeadline;
    if (!deadline || game?.winner) return;
    const delay = Math.max(0, deadline - Date.now()) + 250 + Math.floor(Math.random() * 700);
    const timer = setTimeout(() => {
      if (roomIdRef.current) void api.forceTimeout(roomIdRef.current).catch(() => {});
    }, delay);
    return () => clearTimeout(timer);
  }, [game?.turnDeadline, game?.winner]);

  // Stream-liveness watchdog. The server auto-passes a stalled turn shortly after
  // its deadline, so on a healthy connection the turn always advances soon after
  // turnDeadline passes (which changes this effect's deps and disarms us). If we
  // instead sail well past the deadline with the game still on the same turn, our
  // Listen stream has most likely died silently — a known WebChannel failure on
  // Safari and on tabs the OS suspended in the foreground, where no
  // visibilitychange fires to trigger the reconnect in firebase.ts. Bounce the
  // network so listeners reconnect in seconds instead of the SDK's minutes-long
  // idle timeout, rather than rendering a frozen table.
  useEffect(() => {
    const deadline = game?.turnDeadline;
    if (!deadline || game?.winner) return;
    const STALL_GRACE_MS = 12_000; // safely past the forceTimeout auto-pass window
    const stalled = () =>
      document.visibilityState === 'visible' &&
      navigator.onLine &&
      Date.now() - deadline > STALL_GRACE_MS;
    let interval: ReturnType<typeof setInterval> | null = null;
    const start = setTimeout(() => {
      if (stalled()) void bounceFirestoreNetwork();
      // Keep retrying while still wedged on this same deadline; the effect tears
      // this down the moment a snapshot advances the turn.
      interval = setInterval(() => {
        if (stalled()) void bounceFirestoreNetwork();
      }, 15_000);
    }, Math.max(0, deadline - Date.now()) + STALL_GRACE_MS);
    return () => {
      clearTimeout(start);
      if (interval) clearInterval(interval);
    };
  }, [game?.turnDeadline, game?.winner]);

  const isMyTurn =
    !!game && !game.winner && !!me && game.activeSeat === me.seatIndex && me.alive;
  const mustRespond = !!game && game.pendingAttack?.targetUid === myUid && !game.winner;
  const myFirewall = priv?.hand.find((c) => c.kind === 'firewall') ?? null;
  const myAirGap = priv?.hand.find((c) => c.kind === 'airGap') ?? null;

  const myReach = (() => {
    let reach = 1;
    for (const g of me?.equippedGear ?? []) {
      if (g.kind === 'rig') reach += 1;
      if (g.kind === 'longwave') reach += 2;
    }
    return reach;
  })();

  const ringDistance = useCallback(
    (targetUid: string): number => {
      const ring = players.filter((p) => p.alive).sort((a, b) => a.seatIndex - b.seatIndex);
      const i = ring.findIndex((p) => p.uid === myUid);
      const j = ring.findIndex((p) => p.uid === targetUid);
      if (i < 0 || j < 0) return Number.MAX_SAFE_INTEGER;
      const d = Math.abs(i - j);
      return Math.min(d, ring.length - d);
    },
    [players, myUid],
  );

  const canTarget = useCallback(
    (target: PublicPlayer, card: Card): boolean => {
      if (!isMyTurn || !needsTarget(card.kind)) return false;
      if (!target.alive) return false;
      if (card.kind === 'killSwitch') return target.equippedGear.length > 0;
      if (target.uid === myUid) return false;
      if (card.kind === 'backchannel') return (priv?.hand.length ?? 0) >= 2;
      if (card.kind === 'fieldPatch') {
        return players.filter((p) => p.alive).length > 2 && target.integrity < target.maxIntegrity;
      }
      // The Director can't be locked down (mirrors the server rule).
      if (card.kind === 'downtime' && target.revealedRole === 'director') return false;
      if (card.kind === 'duel' || card.kind === 'downtime' || card.kind === 'trace') return true;
      return ringDistance(target.uid) <= myReach;
    },
    [isMyTurn, myUid, ringDistance, myReach, players, priv?.hand.length],
  );

  // --- Actions --------------------------------------------------------------

  const drawCards = () => submit({ type: 'draw' });
  const playCard = (card: Card, target: PublicPlayer | null, targetCardId?: string, cardIds?: string[]) =>
    submit(
      target
        ? {
            type: 'playCard', cardId: card.id, targetUid: target.uid,
            ...(targetCardId ? { targetCardId } : {}), ...(cardIds ? { cardIds } : {}),
          }
        : { type: 'playCard', cardId: card.id, ...(cardIds ? { cardIds } : {}) },
    );
  const endTurn = () => submit({ type: 'endPhase' });
  const confirmDiscard = (cards: Card[]) =>
    submit({ type: 'discard', cardIds: cards.map((c) => c.id) });
  const respond = (card: Card | null) =>
    submit(card ? { type: 'respond', cardId: card.id } : { type: 'respond' });

  return {
    // state
    screen,
    setScreen,
    busy,
    error,
    setError,
    room,
    players,
    game,
    priv,
    hand: priv?.hand ?? [],
    myRole: priv?.role ?? null,
    fx,
    screenFx,
    // derived
    myUid,
    me,
    isHost,
    isMyTurn,
    mustRespond,
    myFirewall,
    myAirGap,
    myReach,
    canTarget,
    emotes,
    feed,
    spectating,
    // lifecycle
    createRoom,
    quickPlay,
    joinRoom,
    spectate,
    leaveToHome,
    startGame,
    rematch,
    addBot,
    removeBot,
    updateRoomSettings,
    sendEmote,
    sendMessage,
    resumeIfPossible,
    enterTable: () => setScreen('table'),
    // actions
    drawCards,
    playCard,
    endTurn,
    confirmDiscard,
    respond,
  };
}

export type GameSession = ReturnType<typeof useGameSession>;
